import useCustomHook from '@/hooks/useCustomHook';
import { FullscreenContext } from '@/shared/FullscreenContext/FullscreenContext';
import { useContext, useEffect } from 'react';
const startingClipPath = {
  points: [
    [0, 0],
    [0.001, 0],
    [0.001, 0.001],
    [0, 0.001],
  ],
};

const scrollThreshold = 50;

export default function useKaraokePlayer({
  onReady,
  cues,
  currentCue,
  maskPlayerRef,
  karaokeImageRef,
  onStart,
}) {
  const fullscreenContext = useContext(FullscreenContext);
  const { state, actions } = useCustomHook({
    defaultState: {
      maskPlayerRef,
      currentClipPath: null,
      imgNaturalSize: null,
      imageHeight: 0,
      viewPort: null,
      prevScroll: 0,
      scroollTo: 0,
      started: false,
      showScroll: true,
      isFullscreen: false,
      playing: false,
    },
    actions: {
      onPlayerReady: (duration) => {
        if (onReady) onReady(duration);
        console.log(
          'KaraoKePlayer: progressInterval overriden and set to 50ms'
        );
      },
      onSourceImageSized: ({ naturalWidth, naturalHeight }) => {
        actions.setState({
          imgNaturalSize: {
            width: naturalWidth,
            height: naturalHeight,
          },
        });
      },
      onStart: () => {
        const playerBBox = maskPlayerRef.current.getBoundingClientRect();
        const imageBBox = karaokeImageRef.current.getBoundingClientRect();

        actions.setState({
          viewPort: playerBBox,
          imageHeight: imageBBox.height,
          currentClipPath: startingClipPath,
          started: true,
          showScroll: false,
        });
        if (onStart) onStart();
      },
      onCue: (cue) => {
        const newScroll = getMaskScrollValue(
          cue.points,
          state.imageHeight,
          state.viewPort
        );
        const doScroll = state.prevScroll
          ? Math.abs(newScroll - maskPlayerRef.current.scrollTop) >
            scrollThreshold
          : true;

        actions.setState({
          currentClipPath: cue,
          scrollTo: doScroll ? newScroll : state.prevScroll ?? 0,
        });
      },
      onSeek: (time) => {
        if (state.currentClipPath && cues) {
          const foundCue = findCurrentCue(time, cues);
          if (foundCue) {
            actions.onCue(foundCue);
          }
        }
      },
      onPlayPause: (playing) => {
        if (playing) {
          actions.setState({ scrollTo: state.prevScroll ?? 0, playing });
        } else {
          actions.setState({ playing });
        }
      },
      onResize: () => {
        // Necessary for the autoscroll
        const playerBBox = maskPlayerRef.current.getBoundingClientRect();
        const imageBBox = karaokeImageRef.current.getBoundingClientRect();
        actions.setState({
          viewPort: playerBBox,
          imageHeight: imageBBox.height,
        });
      },

      onFullScreen: () => {
        fullscreenContext.actions.setFullscreen(
          !fullscreenContext.state.isFullscreen
        );
      },
      onUserScroll: (event) => {
        if (state.playing) event.preventDefault();
      },
    },
  });
  useEffect(() => {
    if (karaokeImageRef.current) {
      window.addEventListener('resize', actions.onResize);
    }
    return () => {
      window.removeEventListener('resize', actions.onResize);
    };
  }, [karaokeImageRef.current]);

  useEffect(() => {
    actions.setState({ showScroll: !state.playing });
    if (maskPlayerRef.current) {
      maskPlayerRef.current.addEventListener('wheel', actions.onUserScroll);
      maskPlayerRef.current.addEventListener('touchmove', actions.onUserScroll);
    }
    return () => {
      if (maskPlayerRef.current) {
        maskPlayerRef.current.removeEventListener(
          'wheel',
          actions.onUserScroll
        );
        maskPlayerRef.current.removeEventListener(
          'touchmove',
          actions.onUserScroll
        );
      }
    };
  }, [maskPlayerRef.current, state.playing]);

  useEffect(() => {
    if (cues) {
      actions.setState({ currentClipPath: currentCue });
    }
  }, [currentCue]);

  useEffect(() => {
    if (state.scrollTo !== state.prevScroll) {
      maskPlayerRef.current.scrollTo({
        top: state.scrollTo,
        behavior: 'smooth',
      });
      actions.setState({ prevScroll: state.scrollTo });
    }
  }, [maskPlayerRef, state.scrollTo, state.prevScroll]);

  useEffect(() => {
    // Necessary for the autoscroll
    if (maskPlayerRef.current && karaokeImageRef.current) {
      const playerBBox = maskPlayerRef.current.getBoundingClientRect();
      const imageBBox = karaokeImageRef.current.getBoundingClientRect();
      actions.setState({
        viewPort: playerBBox,
        imageHeight: imageBBox.height,
        isFullscreen: fullscreenContext.state.isFullscreen,
      });
    }
  }, [
    fullscreenContext.state.isFullscreen,
    maskPlayerRef.current,
    karaokeImageRef.current,
  ]);

  return { state, actions };
}

function findCurrentCue(time, cues) {
  return cues.find((cue, index) => cues[index + 1]?.time > time);
}

function getMaskScrollValue(path, imageHeight, viewPort) {
  const pointsYPercent = path.map((p) => p[1]);
  const minYPercent = Math.min(...pointsYPercent);
  const scrollY =
    (minYPercent * imageHeight) / 100 - Math.min(20, 0.01 * viewPort.height);

  return path && imageHeight ? scrollY : 0;
}
